import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, X, Building2, UtensilsCrossed, GraduationCap, Factory, ShoppingBag, Sparkles, CheckCircle2, Globe, ArrowRight, Copy, ExternalLink, Layout, Palette } from 'lucide-react'
import { Button } from './ui/button'
import { Logo } from './ui/Logo'
import { useI18n } from '../i18n'
import { useAuth } from '../hooks/use-auth'
import { useWebsite } from '../hooks/use-website'
import { getPublicUrl } from '../helper/website-url'
import type { WebsiteTemplate } from '../types'
import axios from 'axios'

const industries = [
  { id: 'finance', icon: Building2, labelZh: '金融行业', labelEn: 'Finance', color: 'text-blue-500', bg: 'bg-blue-50', ring: 'ring-blue-200' },
  { id: 'food', icon: UtensilsCrossed, labelZh: '餐饮行业', labelEn: 'Food & Beverage', color: 'text-orange-500', bg: 'bg-orange-50', ring: 'ring-orange-200' },
  { id: 'education', icon: GraduationCap, labelZh: '教育培训', labelEn: 'Education', color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-200' },
  { id: 'manufacturing', icon: Factory, labelZh: '制造行业', labelEn: 'Manufacturing', color: 'text-emerald-500', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  { id: 'retail', icon: ShoppingBag, labelZh: '零售电商', labelEn: 'Retail', color: 'text-rose-500', bg: 'bg-rose-50', ring: 'ring-rose-200' },
]

type Step = 'select' | 'template' | 'upload' | 'generating' | 'done'

const stepOrder: Step[] = ['select', 'template', 'upload', 'generating', 'done']

const stepLabels = (t: (zh: string, en: string) => string) => [
  { id: 'select' as Step, label: t('选择行业', 'Industry') },
  { id: 'template' as Step, label: t('选择模板', 'Template') },
  { id: 'upload' as Step, label: t('上传内容', 'Upload') },
  { id: 'generating' as Step, label: t('生成官网', 'Generate') },
]

// Placeholder gradient colors for templates without cover images
const templateGradients = [
  'from-blue-400 to-indigo-500',
  'from-gray-400 to-slate-600',
  'from-orange-400 to-amber-500',
  'from-yellow-400 to-amber-600',
  'from-emerald-400 to-green-600',
  'from-cyan-400 to-blue-500',
  'from-rose-400 to-pink-500',
  'from-violet-400 to-purple-600',
  'from-amber-600 to-orange-700',
  'from-gray-300 to-gray-500',
]

export default function WebsiteGeneratorPage() {
  const navigate = useNavigate()
  const { lang, t } = useI18n()
  const { user } = useAuth()
  const { generateWebsite, pollGenerateStatus, fetchWebsite, website, fetchTemplateList } = useWebsite()

  const [step, setStep] = useState<Step>('select')
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [templates, setTemplates] = useState<WebsiteTemplate[]>([])
  const [templateFilter, setTemplateFilter] = useState<string>('')
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [fileIds, setFileIds] = useState<string[]>([])
  const [companyName, setCompanyName] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const [generatedWebsiteId, setGeneratedWebsiteId] = useState<string | null>(null)
  const [generatedShortcode, setGeneratedShortcode] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch templates when entering template step
  useEffect(() => {
    if (step === 'template' && templates.length === 0) {
      setTemplatesLoading(true)
      fetchTemplateList().then((list) => {
        setTemplates(list)
        setTemplatesLoading(false)
      })
    }
  }, [step, templates.length, fetchTemplateList])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files!)])
    }
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setFileIds((prev) => prev.filter((_, i) => i !== index))
  }

  const uploadFiles = async (): Promise<string[]> => {
    const ids: string[] = []
    for (const file of files) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res: any = await axios.post('/file/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          validateStatus: (status: number) => status >= 200 && status < 400,
        })
        if (res.data?.code === 200 && res.data?.data?.id) {
          ids.push(res.data.data.id)
        }
      } catch {}
    }
    return ids
  }

  const handleGenerate = async () => {
    setUploading(true)
    const uploadedIds = await uploadFiles()
    setFileIds(uploadedIds)
    setUploading(false)

    setStep('generating')
    setProgress(0)
    setProgressMessage(t('正在提交生成任务...', 'Submitting generation task...'))
    setError(null)

    const id = await generateWebsite({
      industryType: selectedIndustry!,
      companyName,
      fileIds: uploadedIds.length > 0 ? uploadedIds : undefined,
      templateId: selectedTemplateId || undefined,
    })

    if (id) {
      setJobId(id)
    } else {
      setError(t('生成任务提交失败，请重试', 'Failed to submit task, please retry'))
      setStep('upload')
    }
  }

  // Poll generation status
  useEffect(() => {
    if (!jobId || step !== 'generating') return

    const interval = setInterval(async () => {
      const job = await pollGenerateStatus(jobId)
      if (!job) return

      setProgress(job.progress || 0)
      setProgressMessage(job.message || '')

      if (job.status === 'COMPLETED' && job.websiteId) {
        setGeneratedWebsiteId(String(job.websiteId))
        fetchWebsite(String(job.websiteId))
        setStep('done')
        clearInterval(interval)
      } else if (job.status === 'FAILED') {
        setError(job.message || t('生成失败，请重试', 'Generation failed, please retry'))
        setStep('upload')
        clearInterval(interval)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [jobId, step])

  // Capture shortcode when website data loads after generation
  useEffect(() => {
    if (website?.shortcode && step === 'done' && !generatedShortcode) {
      setGeneratedShortcode(website.shortcode)
    }
  }, [website, step, generatedShortcode])

  const handleReset = () => {
    setStep('select')
    setSelectedIndustry(null)
    setSelectedTemplateId(null)
    setFiles([])
    setFileIds([])
    setCompanyName('')
    setJobId(null)
    setProgress(0)
    setGeneratedWebsiteId(null)
    setGeneratedShortcode(null)
    setCopied(false)
    setError(null)
  }

  // Filter templates by industry
  const filteredTemplates = templateFilter
    ? templates.filter(t => t.industryType?.includes(templateFilter) || t.industryType === 'general')
    : templates

  const currentIdx = stepOrder.indexOf(step)

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #f0efff 30%, #f5f0ff 60%, #faf8ff 100%)' }}>
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-14">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-8 shrink-0">
            <Logo size={24} />
            <span className="text-[#1a1a1a]">PrimeBook</span>
            <span className="text-orange-500">AI</span>
          </Link>
          <button onClick={() => navigate('/')} className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
            {t('返回首页', 'Back')}
          </button>

          <div className="hidden sm:flex items-center gap-2 ml-auto">
            {stepLabels(t).map((s, i) => {
              const thisIdx = stepOrder.indexOf(s.id)
              const isActive = thisIdx === currentIdx
              const isDone = thisIdx < currentIdx
              return (
                <div key={s.id} className="flex items-center gap-2">
                  {i > 0 && <div className={`w-8 h-[1px] ${isDone ? 'bg-violet-300' : 'bg-gray-200'}`} />}
                  <div className={`flex items-center gap-1.5 text-[12px] ${isActive ? 'text-violet-500 font-medium' : isDone ? 'text-violet-400' : 'text-gray-300'}`}>
                    <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      isDone ? 'bg-violet-500 text-white' : isActive ? 'bg-violet-100 text-violet-500' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isDone ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                    </div>
                    <span className="hidden md:inline">{s.label}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Step 1: Select Industry */}
        {step === 'select' && (
          <div className="animate-in fade-in duration-500 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 text-xs text-violet-500 font-medium mb-4">
                <Sparkles className="h-3.5 w-3.5" />
                {t('一键生成', 'One-Click Generate')}
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
                {t('选择您的行业', 'Choose Your Industry')}
              </h1>
              <p className="text-sm text-gray-400 max-w-md mx-auto">
                {t('AI将根据行业特点为您生成专属企业官网，并自动配置AI管家', 'AI will generate a dedicated website tailored to your industry with an AI Butler')}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-10">
              {industries.map((ind) => {
                const selected = selectedIndustry === ind.id
                return (
                  <button key={ind.id} onClick={() => setSelectedIndustry(ind.id)}
                    className={`group flex flex-col items-center gap-2 p-5 rounded-2xl border-2 transition-all duration-300 ${
                      selected ? `${ind.bg} ${ind.ring} border-current shadow-sm` : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-sm'
                    }`}>
                    <div className={`h-10 w-10 rounded-xl ${ind.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <ind.icon className={`h-5 w-5 ${ind.color}`} />
                    </div>
                    <span className={`text-xs font-medium ${selected ? ind.color : 'text-gray-500'}`}>
                      {lang === 'zh' ? ind.labelZh : ind.labelEn}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium text-[#1a1a1a] mb-2">{t('企业名称', 'Company Name')}</label>
              <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t('请输入企业名称', 'Enter company name')}
                className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-white text-sm text-[#1a1a1a] placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all" />
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 text-sm text-red-500">{error}</div>
            )}

            <div className="flex justify-end">
              <Button size="lg" disabled={!selectedIndustry || !companyName.trim()}
                className="bg-[#1a1a1a] text-white hover:bg-[#333] px-8 h-12 text-sm font-medium rounded-xl disabled:opacity-40"
                onClick={() => { setError(null); setStep('template') }}>
                {t('下一步', 'Next')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Template Selection */}
        {step === 'template' && (
          <div className="animate-in fade-in duration-500">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
                {t('选择官网模板', 'Choose a Template')}
              </h1>
              <p className="text-sm text-gray-400 max-w-md mx-auto">
                {t('选择一个模板风格，AI将基于模板结构填充您的企业信息', 'Pick a template style — AI will fill it with your company info')}
              </p>
            </div>

            {/* Industry filter pills */}
            <div className="flex flex-wrap items-center gap-2 mb-6 justify-center">
              <button onClick={() => setTemplateFilter('')}
                className={`px-3 py-1.5 text-xs rounded-full transition-all ${!templateFilter ? 'bg-violet-500 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'}`}>
                {t('全部', 'All')}
              </button>
              {industries.map(ind => (
                <button key={ind.id} onClick={() => setTemplateFilter(templateFilter === ind.id ? '' : ind.id)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${templateFilter === ind.id ? 'bg-violet-500 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'}`}>
                  {lang === 'zh' ? ind.labelZh : ind.labelEn}
                </button>
              ))}
            </div>

            {templatesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="rounded-2xl border border-gray-100 bg-white overflow-hidden animate-pulse">
                    <div className="h-40 bg-gray-100" />
                    <div className="p-4">
                      <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-16 mb-8">
                <Layout className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">{t('暂无模板，将使用AI自由生成', 'No templates available — AI will generate freely')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {filteredTemplates.map((tmpl, i) => {
                  const selected = selectedTemplateId === tmpl.id
                  const gradient = templateGradients[i % templateGradients.length]
                  return (
                    <button key={tmpl.id}
                      onClick={() => setSelectedTemplateId(tmpl.id)}
                      className={`group rounded-2xl border-2 bg-white overflow-hidden text-left transition-all duration-300 ${
                        selected ? 'border-orange-500 shadow-md shadow-orange-500/10' : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                      }`}>
                      {/* Cover */}
                      <div className="relative h-40 overflow-hidden">
                        {tmpl.coverUrl ? (
                          <img src={tmpl.coverUrl} alt={tmpl.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                            <Palette className="h-8 w-8 text-white/40" />
                          </div>
                        )}
                        {selected && (
                          <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-orange-500 flex items-center justify-center">
                            <CheckCircle2 className="h-4 w-4 text-white" />
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-[#1a1a1a] mb-1">
                          {lang === 'zh' ? tmpl.name : (tmpl.nameEn || tmpl.name)}
                        </h3>
                        {(tmpl.description || tmpl.descriptionEn) && (
                          <p className="text-xs text-gray-400 line-clamp-2">
                            {lang === 'zh' ? tmpl.description : (tmpl.descriptionEn || tmpl.description)}
                          </p>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}

            <div className="flex justify-between max-w-3xl mx-auto">
              <Button size="lg" variant="outline"
                className="border-black/[0.08] text-gray-500 hover:bg-white hover:text-gray-900 px-6 h-12 text-sm rounded-xl"
                onClick={() => setStep('select')}>
                {t('上一步', 'Back')}
              </Button>
              <Button size="lg"
                className="bg-[#1a1a1a] text-white hover:bg-[#333] px-8 h-12 text-sm font-medium rounded-xl"
                onClick={() => { setError(null); setStep('upload') }}>
                {selectedTemplateId ? t('下一步', 'Next') : t('跳过，AI自由生成', 'Skip, AI generates freely')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Upload */}
        {step === 'upload' && (
          <div className="animate-in fade-in duration-500 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
                {t('上传企业内容', 'Upload Company Content')}
              </h1>
              <p className="text-sm text-gray-400 max-w-md mx-auto">
                {t('上传产品手册、品牌介绍、服务项目等，AI将据此生成官网和配置AI管家', 'Upload docs — AI generates your website and configures the AI Butler from them')}
              </p>
            </div>

            <div className="relative border-2 border-dashed border-gray-200 rounded-2xl bg-white/60 p-8 text-center hover:border-violet-300 hover:bg-violet-50/30 transition-all duration-300 cursor-pointer mb-6"
              onClick={() => fileInputRef.current?.click()}>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md"
                className="hidden" onChange={handleFileChange} />
              <Upload className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400 mb-1">{t('点击或拖拽文件到此处', 'Click or drag files here')}</p>
              <p className="text-[11px] text-gray-300">{t('支持 PDF / Word / Excel / TXT / Markdown', 'PDF / Word / Excel / TXT / Markdown supported')}</p>
            </div>

            {files.length > 0 && (
              <div className="space-y-2 mb-8">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-black/[0.04]">
                    <div className="h-8 w-8 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                      <Upload className="h-4 w-4 text-violet-400" />
                    </div>
                    <span className="text-sm text-[#1a1a1a] truncate flex-1">{file.name}</span>
                    <span className="text-[11px] text-gray-300 shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeFile(i)} className="text-gray-300 hover:text-red-400 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 text-sm text-red-500">{error}</div>
            )}

            <div className="flex justify-between">
              <Button size="lg" variant="outline"
                className="border-black/[0.08] text-gray-500 hover:bg-white hover:text-gray-900 px-6 h-12 text-sm rounded-xl"
                onClick={() => setStep('template')}>
                {t('上一步', 'Back')}
              </Button>
              <Button size="lg"
                className="bg-[#1a1a1a] text-white hover:bg-[#333] px-8 h-12 text-sm font-medium rounded-xl"
                onClick={handleGenerate} disabled={uploading}>
                <Sparkles className="mr-2 h-4 w-4" />
                {uploading ? t('上传文件中...', 'Uploading...') : t('一键生成官网', 'Generate Website')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Generating */}
        {step === 'generating' && (
          <div className="animate-in fade-in duration-500 text-center py-20 max-w-3xl mx-auto">
            <div className="relative mx-auto mb-8">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center mx-auto">
                <Sparkles className="h-8 w-8 text-violet-500 animate-pulse" />
              </div>
              <div className="absolute inset-0 h-20 w-20 rounded-full border-2 border-violet-200 mx-auto animate-ping opacity-30" />
            </div>
            <h2 className="text-xl font-semibold text-[#1a1a1a] mb-3">
              {t('AI正在为您生成官网...', 'AI is generating your website...')}
            </h2>
            <p className="text-sm text-gray-400 max-w-sm mx-auto mb-8">
              {progressMessage || t('正在分析企业信息，设计页面结构，填充专业内容', 'Analyzing company info, designing page structure, filling professional content')}
            </p>
            <div className="max-w-xs mx-auto">
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-indigo-400 transition-all duration-500"
                  style={{ width: `${Math.max(progress, 10)}%` }} />
              </div>
              <p className="text-[11px] text-gray-300 mt-2">{progress}%</p>
            </div>
          </div>
        )}

        {/* Step 5: Done */}
        {step === 'done' && (
          <div className="animate-in fade-in duration-500 text-center py-12 max-w-3xl mx-auto">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            </div>
            <h2 className="text-xl font-semibold text-[#1a1a1a] mb-3">
              {t('官网生成完成！', 'Website Generated!')}
            </h2>
            <p className="text-sm text-gray-400 max-w-sm mx-auto mb-8">
              {t('您的企业官网已准备就绪，可以进入编辑器继续调整或直接发布', 'Your website is ready. Edit it further or publish directly')}
            </p>

            <div className="max-w-sm mx-auto mb-8 rounded-2xl border border-black/[0.06] bg-white shadow-sm overflow-hidden">
              <div className="h-32 bg-gradient-to-br from-violet-100 via-indigo-50 to-sky-50 flex items-center justify-center">
                <Globe className="h-10 w-10 text-violet-300" />
              </div>
              <div className="p-5 text-left">
                <h3 className="text-base font-bold text-[#1a1a1a] mb-1">{companyName || t('我的企业', 'My Company')}</h3>
                <p className="text-xs text-gray-400 mb-3">
                  {industries.find(i => i.id === selectedIndustry)?.[lang === 'zh' ? 'labelZh' : 'labelEn'] || ''} · {t('AI生成官网', 'AI Generated Website')}
                </p>
                <div className="flex gap-2 mb-4">
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-50 text-emerald-500 font-medium">{t('草稿', 'Draft')}</span>
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-violet-50 text-violet-500 font-medium">{t('AI管家已配置', 'AI Butler Ready')}</span>
                </div>
                {generatedShortcode && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                    <Globe className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-600 truncate flex-1 select-all">{getPublicUrl(generatedShortcode)}</span>
                    <button
                      className="shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                      title={t('复制链接', 'Copy link')}
                      onClick={() => {
                        navigator.clipboard.writeText(getPublicUrl(generatedShortcode))
                        setCopied(true)
                        setTimeout(() => setCopied(false), 2000)
                      }}>
                      <Copy className={`h-3.5 w-3.5 ${copied ? 'text-emerald-500' : 'text-gray-400'}`} />
                    </button>
                    <a href={`/e/${generatedShortcode}`} target="_blank" rel="noopener noreferrer"
                      className="shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                      title={t('打开官网', 'Open website')}>
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button size="lg"
                className="bg-[#1a1a1a] text-white hover:bg-[#333] px-8 h-12 text-sm font-medium rounded-xl"
                onClick={() => generatedWebsiteId ? navigate(`/website/${generatedWebsiteId}/edit`) : navigate('/my-website')}>
                {t('进入编辑器', 'Open Editor')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              {generatedShortcode && (
                <Button size="lg" variant="outline"
                  className="border-black/[0.08] text-gray-500 hover:bg-white hover:text-gray-900 px-6 h-12 text-sm rounded-xl"
                  onClick={() => window.open(`/e/${generatedShortcode}`, '_blank')}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('查看官网', 'View Website')}
                </Button>
              )}
              <Link to="/my-website"
                className="px-5 py-2.5 text-sm text-gray-500 hover:text-[#1a1a1a] transition-colors">
                {t('我的官网', 'My Website')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
